import { RenderBatch } from '../scene/RenderBatch';
import { SampleRing } from '../scene/SampleRing';

// Max value: https://www.w3.org/TR/webgpu/#queryset-creation
const MAX_QUERY_COUNT = 4096;
// Somewhat arbitrary size.
const SAMPLE_COUNT = 5;

export class TimingManager {

  /** @type {GPUDevice} */
  #device;

  /** @type {GPUQuerySet} */
  #querySet;
  /** @type {GPUBuffer} */
  #queryBuffer;
  /** @type {GPUBuffer[]} */
  #unmappedBuffers = [];
  /** @type {GPUBuffer|null} */
  #readBuffer = null;


  /** @type {number} */
  #nextIndex = 0;
  /** @type {number} */
  #resolvedQueries = 0;

  /** @type {[string, number][]} */
  #passQueries = [];
  /** @type {[RenderBatch, number][]} */
  #batchQueries = [];

  /** @type {[string, number][]} */
  #passTimes = [];
  /** @type {number} */
  #totalBatchTime = 0;
  /** @type {number} */
  #totalTime = 0;

  /** @param {GPUDevice} device */
  constructor(device) {
    this.#device = device;
    this.#querySet = device.createQuerySet({
      type: 'timestamp',
      count: MAX_QUERY_COUNT
    });
    this.#queryBuffer = device.createBuffer({
      label: 'query resolve buffer',
      size: 8 * this.#querySet.count,
      usage: GPUBufferUsage.COPY_SRC | GPUBufferUsage.QUERY_RESOLVE
    });
  }

  /**
   * Whether we can make another query, based on capacity.
   * @returns {boolean}
   */
  #canQuery() {
    if (this.#nextIndex + 1 >= this.#querySet.count) {
      return false;
    }
    return true;
  }

  /**
   * @returns {GPURenderPassTimestampWrites}
   */
  #newQuery() {
    /** @type {GPURenderPassTimestampWrites} */
    const timestampWrites = {
      querySet: this.#querySet,
      beginningOfPassWriteIndex: this.#nextIndex,
      endOfPassWriteIndex: this.#nextIndex + 1
    };
    this.#nextIndex += 2;

    return timestampWrites;
  }

  /**
   * If possible, adds timestampWrites entry to descriptor and tracks the
   * timestamp query based on the descriptor's label to resolve later.
   * @param {GPURenderPassDescriptor} descriptor
   */
  newPassQuery(descriptor) {
    if (!this.#canQuery()) {
      return;
    }

    this.#passQueries.push([descriptor.label, this.#nextIndex]);
    descriptor.timestampWrites = this.#newQuery();
  }

  /**
   * If possible, adds timestampWrites entry to descriptor and tracks the
   * timestamp query based on the render batch to resolve later.
   * @param {GPURenderPassDescriptor} descriptor
   * @param {RenderBatch} batch
   */
  newBatchQuery(descriptor, batch) {
    if (!this.#canQuery()) {
      return;
    }

    this.#batchQueries.push([batch, this.#nextIndex]);
    descriptor.timestampWrites = this.#newQuery();
  }

  /**
   * Gets an unmapped buffer from the queue, or makes one if none are available.
   * @returns {GPUBuffer}
   */
  #getUnmappedBuffer() {
    if (this.#unmappedBuffers.length) {
      return this.#unmappedBuffers.shift();
    } else {
      return this.#device.createBuffer({
        label: 'query read buffer',
        size: this.#queryBuffer.size,
        usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
      });
    }
  }

  /**
   * Resolve queries and copy them into a buffer for reading.
   */
  resolveQueries() {
    if (this.#nextIndex === 0) {
      return;
    }

    this.#readBuffer = this.#getUnmappedBuffer();
    if (this.#readBuffer.mapState !== 'unmapped') {
      console.error('TimingManager read buffer is not unmapped!');
      this.#readBuffer = null;
      return;
    }

    const encoder = this.#device.createCommandEncoder({ label: 'query encoder' });

    this.#resolvedQueries = this.#nextIndex;
    encoder.resolveQuerySet(
      this.#querySet, 0, this.#resolvedQueries, this.#queryBuffer, 0);

    encoder.copyBufferToBuffer(
      this.#queryBuffer, 0, this.#readBuffer, 0, 8 * this.#resolvedQueries);

    this.#device.queue.submit([encoder.finish()]);
  }

  /**
   * If there are resolved queries, map the read buffer and calculate timings
   * when the buffer is mapped.
   */
  readQueries() {
    const readBuffer = this.#readBuffer;
    const resolvedQueries = this.#resolvedQueries;
    const passQueries = this.#passQueries;
    const batchQueries = this.#batchQueries;

    this.#readBuffer = null;
    this.#resolvedQueries = 0;
    this.#passQueries = [];
    this.#batchQueries = [];
    this.#nextIndex = 0;

    if (resolvedQueries === 0) {
      return;
    }
    if (readBuffer === null) {
      return;
    }

    readBuffer.mapAsync(GPUMapMode.READ).then(() => {
      const timestamps = new BigInt64Array(
        readBuffer.getMappedRange(0, 8 * resolvedQueries));

      this.#passTimes = [];
      for (const [passName, beginIndex] of passQueries) {
        // Convert to float, then convert ns to ms.
        const duration = Number(timestamps[beginIndex + 1] - timestamps[beginIndex]) / 1_000_000;
        this.#passTimes.push([passName, duration]);
      }

      this.#totalBatchTime = 0;
      let batchTotals = new Map();
      for (const [batch, beginIndex] of batchQueries) {
        // Convert to float, then convert ns to ms.
        const duration = Number(timestamps[beginIndex + 1] - timestamps[beginIndex]) / 1_000_000;
        this.#totalBatchTime += duration;
        const total = batchTotals.get(batch) || 0;
        batchTotals.set(batch, total + duration);
      }

      batchTotals.forEach((total, batch) => {
        // Timestamps are quantized to prevent fingerprinting, and are
        // not guaranteed to be monotonic. Some bogus values can result,
        // which we should ignore.
        if (total <= 0) {
          return;
        }
        if (!batch.gpuFrameSampler) {
          batch.gpuFrameSampler = new SampleRing(SAMPLE_COUNT);
        }
        batch.gpuFrameSampler.addSample(total);
      });

      let minStart = timestamps[0];
      let maxEnd = timestamps[1];
      for (let i = 2; i < resolvedQueries - 1; i += 2) {
        if (minStart > timestamps[i]) {
          minStart = timestamps[i];
        }
        if (maxEnd < timestamps[i + 1]) {
          maxEnd = timestamps[i + 1];
        }
      }

      // Note: This will often be shorter than #totalBatchTime because it
      // appears that time ranges often overlap one another.
      // Convert to float, then convert ns to ms.
      this.#totalTime = Number(maxEnd - minStart) / 1_000_000;

      readBuffer.unmap();
      this.#unmappedBuffers.push(readBuffer);
    });
  }

  /**
   * Prints all pass times, total batch time, and total frame time.
   */
  printTimes() {
    for (const [passName, time] of this.#passTimes) {
      console.log(`pass: ${passName}, time: ${time}`);
    }
    console.log(`total batch time: ${this.#totalBatchTime}`);
    console.log(`total frame time: ${this.#totalTime}`);
  }

  totalFrameTime() {
    return this.#totalTime;
  }

}